import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import AdminLayout from "./components/AdminLayout";
import CustomerLayout from "./components/CustomerLayout";
import { Currency } from "./lib/currency";
import { captureUtm, track } from "./lib/tracking";
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Category from "./pages/Category";
import Collection from "./pages/Collection";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import OrderPayment from "./pages/OrderPayment";
import PaymentSubmitted from "./pages/PaymentSubmitted";
import OrderLookup from "./pages/OrderLookup";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import HelpCenter from "./pages/HelpCenter";
import Compare from "./pages/Compare";
import AccountLogin from "./pages/account/Login";
import Register from "./pages/account/Register";
import Account from "./pages/account/Account";
import Profile from "./pages/account/Profile";
import Addresses from "./pages/account/Addresses";
import AccountOrders from "./pages/account/Orders";
import AccountOrderDetail from "./pages/account/OrderDetail";
import Messages from "./pages/account/Messages";
import AccountTickets from "./pages/account/Tickets";
import Wishlist from "./pages/account/Wishlist";
import Returns from "./pages/account/Returns";
import AdminLogin from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import ProductForm from "./pages/admin/ProductForm";
import ProductImport from "./pages/admin/ProductImport";
import ProductExport from "./pages/admin/ProductExport";
import Categories from "./pages/admin/Categories";
import Orders from "./pages/admin/Orders";
import OrderDetail from "./pages/admin/OrderDetail";
import PaymentMethods from "./pages/admin/PaymentMethods";
import PaymentProofs from "./pages/admin/PaymentProofs";
import Customers from "./pages/admin/Customers";
import Banners from "./pages/admin/Banners";
import Homepage from "./pages/admin/Homepage";
import AdminPages from "./pages/admin/Pages";
import Languages from "./pages/admin/Languages";
import Currencies from "./pages/admin/Currencies";
import Settings from "./pages/admin/Settings";
import Pixels from "./pages/admin/Pixels";
import ActivityLogs from "./pages/admin/ActivityLogs";
import AdminUsers from "./pages/admin/AdminUsers";
import MediaLibrary from "./pages/admin/MediaLibrary";
import AdminSeo from "./pages/admin/Seo";
import Setup from "./pages/admin/Setup";
import PageSeo from "./components/Seo";

function Storefront({ currency, setCurrency }: { currency: Currency; setCurrency: (v: Currency) => void }) {
  const [cartOpen, setCartOpen] = useState(false);
  return (
    <>
      <PageSeo title="SellerSupply Shop" description="Shop curated products with multilingual browsing, simple checkout, and clear manual payment instructions." />
      <Header currency={currency} onCurrency={setCurrency} onCart={() => setCartOpen(true)} />
      <Routes>
        <Route index element={<Home currency={currency} />} />
        <Route path="products" element={<Products currency={currency} />} />
        <Route path="products/:slug" element={<ProductDetail currency={currency} />} />
        <Route path="category/:slug" element={<Category currency={currency} />} />
        <Route path="collections/:slug" element={<Collection currency={currency} />} />
        <Route path="cart" element={<Cart currency={currency} />} />
        <Route path="checkout" element={<Checkout currency={currency} />} />
        <Route path="order-success" element={<OrderSuccess />} />
        <Route path="order-payment/:orderNo" element={<OrderPayment />} />
        <Route path="order-lookup" element={<OrderLookup />} />
        <Route path="payment-submitted" element={<PaymentSubmitted />} />
        <Route path="login" element={<AccountLogin />} />
        <Route path="register" element={<Register />} />
        <Route path="account" element={<CustomerLayout />}>
          <Route index element={<Account />} />
          <Route path="profile" element={<Profile />} />
          <Route path="addresses" element={<Addresses />} />
          <Route path="orders" element={<AccountOrders />} />
          <Route path="orders/:orderNo" element={<AccountOrderDetail />} />
          <Route path="messages" element={<Messages />} />
          <Route path="tickets" element={<AccountTickets />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="returns" element={<Returns />} />
        </Route>
        <Route path="about" element={<About />} />
        <Route path="contact" element={<Contact />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="refund-policy" element={<RefundPolicy />} />
        <Route path="shipping-policy" element={<ShippingPolicy />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogDetail />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="help/:slug" element={<HelpCenter />} />
        <Route path="compare" element={<Compare />} />
      </Routes>
      <Footer />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} currency={currency} />
    </>
  );
}

export default function App() {
  const [currency, setCurrency] = useState<Currency>((localStorage.getItem("currency") as Currency) || "USD");
  const location = useLocation();
  useEffect(() => { localStorage.setItem("currency", currency); }, [currency]);
  useEffect(() => { captureUtm(); track("PageView", { path: location.pathname }); }, [location.pathname]);
  const savedLang = localStorage.getItem("lang");
  const browserLang = navigator.language.toLowerCase();
  const autoLang = savedLang || (browserLang.startsWith("zh") ? "zh" : browserLang.startsWith("es") ? "es" : browserLang.startsWith("ru") ? "ru" : "en");
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${autoLang}`} replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/setup" element={<AdminLayout />}><Route index element={<Setup />} /></Route>
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="homepage" element={<Homepage />} />
        <Route path="homepage/sections" element={<Homepage />} />
        <Route path="banners" element={<Banners />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/new" element={<ProductForm />} />
        <Route path="products/:id/edit" element={<ProductForm />} />
        <Route path="products/import" element={<ProductImport />} />
        <Route path="products/export" element={<ProductExport />} />
        <Route path="categories" element={<Categories />} />
        <Route path="tags" element={<Categories />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="payment-methods" element={<PaymentMethods />} />
        <Route path="payment-reviews" element={<PaymentProofs />} />
        <Route path="customers" element={<Customers />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="policies" element={<AdminPages />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/general" element={<Settings />} />
        <Route path="settings/languages" element={<Languages />} />
        <Route path="settings/currency" element={<Currencies />} />
        <Route path="settings/theme" element={<Settings />} />
        <Route path="settings/seo" element={<AdminSeo />} />
        <Route path="settings/pixel" element={<Pixels />} />
        <Route path="settings/live-chat" element={<Settings />} />
        <Route path="activity-logs" element={<ActivityLogs />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="export" element={<ProductExport />} />
      </Route>
      <Route path="/:lang/*" element={<Storefront currency={currency} setCurrency={setCurrency} />} />
    </Routes>
  );
}
